import React from 'react';
import {Column} from 'react-table';
import {makeStyles} from '@material-ui/core/styles';
import {TFunction} from 'react-i18next';
import {Colors} from '../../../../styles/Colors';
import {TransactionItem} from '../../../../store/actions/accounts/payloads';
import TwoLineCell from '../../../../components/Calls/TwoLineCell';
import CustomizedIconButton from '../../../../components/IconButton/IconButton';
import {Assignment} from '@material-ui/icons';
import dayjs from '../../../../services/customDayJs';
import {convertFormatFromBackendToDayJs} from '../../../../utils/dateWithTimezoneConversion';
import OverflowTooltip from '../../../../components/OverflowTooltip/OverflowTooltip';

export const useStyles = makeStyles(() => ({
    container: {
        maxWidth: 1040,
        padding: '22px 16px',
    },
    filtersRow: {
        display: 'flex',
        alignItems: 'flex-start',
        maxWidth: 990,
        marginBottom: 24,

        '& .MuiFormControl-root': {
            marginRight: 18,
        },

        '& .date-time-picker': {
            '& .MuiInputBase-input': {
                width: 208,
            },
        },
    },
    searchButton: {
        minWidth: 96,
        marginTop: 8,
    },
    tableContainer: {
        maxWidth: 1040,

        '& .MuiTableRow-root': {
            padding: '0 20px',
        },

        '& .MuiTableBody-root .MuiTableCell-root': {
            alignItems: 'center',
        },
    },
    chargeCell: {
        fontWeight: 500,
        textAlign: 'right' as const,
        display: 'block',
        width: '100%',
    },
    rightAlignHeader: {
        textAlign: 'right' as const,
        display: 'block',
        width: '100%',
    },
    rightAlignCell: {
        textAlign: 'right' as const,
        display: 'block',
        width: '100%',
    },
    dateCell: {
        display: 'flex',
        flexDirection: 'column',
    },
    dateTime: {
        fontWeight: 500,
    },
    dateDate: {
        fontSize: 12,
        color: Colors.Gray5,
    },
}));

// Internal services — matched by i_service directly
const INTERNAL_SERVICE_IDS = new Set([4, 1, 12, 15]);

// Service type groups for regular services
const DESTINATION_SERVICE_TYPES = new Set([3, 10, 14]); // Voice, Messaging, Conference
const CLD_SERVICE_TYPES = new Set([20, 16]); // Voice Recording, IPTV
const AMOUNT_SERVICE_TYPES = new Set([2, 5, 7, 17, 18]); // Quantity, Data, Measured, Session, Service Pool

export function getAdditionalInfo(item: TransactionItem): string {
    // Path 1: Internal services — show CLD
    if (item.i_service !== undefined && INTERNAL_SERVICE_IDS.has(item.i_service)) {
        return item.CLD ?? '—';
    }

    // Path 2: Regular services — dispatch on i_service_type
    if (item.i_service_type !== undefined) {
        if (DESTINATION_SERVICE_TYPES.has(item.i_service_type)) {
            return item.destination ?? '—';
        }
        if (CLD_SERVICE_TYPES.has(item.i_service_type)) {
            return item.CLD ?? '—';
        }
        if (AMOUNT_SERVICE_TYPES.has(item.i_service_type)) {
            const parts: string[] = [];
            if (item.amount !== undefined) parts.push(String(item.amount));
            if (item.unit) parts.push(item.unit);
            return parts.length > 0 ? parts.join(' ') : '—';
        }
        // Dialup, Net Access, Wi-Fi, Mobile Provision (6, 8, 9, 19) — nothing shown
        return '—';
    }

    return item.CLD ?? '—';
}

export const generateTransactionColumns = (
    t: TFunction<string>,
    classes: ReturnType<typeof useStyles>,
    onClickDetails: (item: TransactionItem) => void,
    userDateFormat?: string,
    customerCurrency?: string,
    hideActions?: boolean,
): Column<TransactionItem>[] => {
    const dateFormat = userDateFormat
        ? convertFormatFromBackendToDayJs(userDateFormat)
        : 'MMM DD, YYYY';

    return [
    {
        accessor: 'bill_time',
        Header: t<string>('screens:accounts.transactionDate'),
        width: 0.8,
        Cell: function Cell({row}) {
            const billTime = row.original.bill_time;
            const date = billTime ? dayjs(billTime) : null;
            if (!date || !date.isValid()) return <span>—</span>;
            return (
                <div className={classes.dateCell}>
                    <span className={classes.dateTime}>{date.format('HH:mm:ss')}</span>
                    <span className={classes.dateDate}>{date.format(dateFormat)}</span>
                </div>
            );
        },
    },
    {
        accessor: 'service',
        Header: t<string>('screens:accounts.serviceDetails'),
        width: 1.2,
        Cell: function Cell({row}) {
            const destination = row.original.destination;
            const showDestination = destination && destination !== '|';
            return (
                <TwoLineCell
                    headerText={row.original.service ?? '—'}
                    descriptionText={showDestination ? destination : undefined}
                />
            );
        },
    },
    {
        accessor: 'description',
        Header: t<string>('screens:accounts.description'),
        width: 1.2,
        Cell: function Cell({row}) {
            const desc = row.original.description ?? '—';
            return <OverflowTooltip text={desc} tooltip={desc} copy={false}/>;
        },
    },
    {
        accessor: 'CLD',
        Header: <span className={classes.rightAlignHeader}>{t<string>('screens:accounts.additionalInfo')}</span>,
        width: 1,
        Cell: function Cell({row}) {
            const info = getAdditionalInfo(row.original);
            return <span className={classes.rightAlignCell}>{info}</span>;
        },
    },
    {
        accessor: 'charged_amount',
        Header: <span className={classes.rightAlignHeader}>{t('screens:calls.charge') + (customerCurrency ? `, ${customerCurrency}` : '')}</span>,
        width: 0.7,
        Cell: function Cell({row}) {
            const amount = row.original.charged_amount;
            return (
                <span className={classes.chargeCell}>
                    {amount !== undefined ? amount.toFixed(2) : '—'}
                </span>
            );
        },
    },
    ...(!hideActions ? [{
        accessor: 'i_xdr' as const,
        Header: t<string>('screens:accounts.actions'),
        width: 0.5,
        Cell: function Cell({row}: { row: { original: TransactionItem } }) {
            return (
                <CustomizedIconButton
                    tooltipText={t('screens:accounts.viewDetails')}
                    dataQa="transaction-details-icon"
                    onClick={() => onClickDetails(row.original)}
                    skipPermission
                >
                    <Assignment/>
                </CustomizedIconButton>
            );
        },
    }] : []),
];
};
